// ===========================================
// Locations Management Page
// ===========================================
// CRUD interface for managing school locations

import { useState, useMemo } from 'react';
import {
  Box,
  TextField,
  Alert,
  Chip,
  IconButton,
  FormControlLabel,
  Switch,
  Stack,
  Autocomplete,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Tooltip,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import PlaceIcon from '@mui/icons-material/Place';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useSnackbar } from 'notistack';
import PageHeader from '../../components/common/PageHeader';
import DataTable, { Column } from '../../components/common/DataTable';
import FormModal from '../../components/common/FormModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import {
  useLocations,
  useCreateLocation,
  useUpdateLocation,
  useDeleteLocation,
} from '../../hooks/useAdmin';
import { useCountries } from '../../hooks/useCountries';
import { Location } from '../../api/admin.api';
import { COUNTRIES, formatAddress } from '../../constants/countries';

// ===========================================
// TYPES
// ===========================================

interface LocationFormData {
  name: string;
  streetAddress: string;
  unit: string;
  suburb: string;
  state: string;
  postcode: string;
  country: string;
  phone: string;
  isActive: boolean;
}

const DEFAULT_FORM_DATA: LocationFormData = {
  name: '',
  streetAddress: '',
  unit: '',
  suburb: '',
  state: '',
  postcode: '',
  country: 'AU',
  phone: '',
  isActive: true,
};

// ===========================================
// COMPONENT
// ===========================================

export default function LocationsPage() {
  const { data: locations, isLoading, error } = useLocations();
  const { data: countriesData } = useCountries();
  // Guard against a non-array response (e.g. nginx serving index.html for the
  // public API): the `= COUNTRIES` default only covers undefined, not a bad shape.
  const countries = Array.isArray(countriesData) ? countriesData : COUNTRIES;
  const createMutation = useCreateLocation();
  const updateMutation = useUpdateLocation();
  const { enqueueSnackbar } = useSnackbar();
  const [regeocodingId, setRegeocodingId] = useState<string | null>(null);

  // "Update map pin" — re-geocode the stored address. A plain re-save with an
  // unchanged address does NOT geocode (location.service.ts only recomputes on
  // an address/name change, a manual pin, or this flag), so locations created
  // before geocoding shipped stay unmapped until someone presses this.
  const handleUpdateMapPin = async (location: Location) => {
    setRegeocodingId(location.id);
    try {
      const updated = await updateMutation.mutateAsync({ id: location.id, data: { regeocode: true } });
      if (typeof updated?.latitude === 'number' && typeof updated?.longitude === 'number') {
        enqueueSnackbar(`Map pin updated (${updated.latitude.toFixed(5)}, ${updated.longitude.toFixed(5)})`, { variant: 'success' });
      } else {
        enqueueSnackbar('No map pin found for this address — check the street, suburb and postcode.', { variant: 'warning' });
      }
    } catch {
      enqueueSnackbar('Could not update the map pin', { variant: 'error' });
    } finally {
      setRegeocodingId(null);
    }
  };
  const deleteMutation = useDeleteLocation();

  // Filter state
  const [hideInactive, setHideInactive] = useState(false);

  // Form state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [formData, setFormData] = useState<LocationFormData>(DEFAULT_FORM_DATA);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState<Location | null>(null);

  // Blocking classes error state
  const [blockingClasses, setBlockingClasses] = useState<{ id: string; name: string }[] | null>(null);
  const [copied, setCopied] = useState(false);

  // Get country config for dynamic labels (from API data with static fallback)
  const selectedCountry = countries.find((c) => c.code === formData.country);
  const stateLabel = selectedCountry?.stateLabel || 'State';
  const postcodeLabel = selectedCountry?.postcodeLabel || 'Postcode';
  const stateOptions = selectedCountry?.states || [];

  // Filter locations
  const filteredLocations = useMemo(() => {
    if (!locations) return [];

    if (hideInactive) {
      return locations.filter((location) => location.isActive);
    }

    return locations;
  }, [locations, hideInactive]);

  // Table columns
  const columns: Column<Location>[] = [
    {
      id: 'name',
      label: 'Name',
      minWidth: 150,
    },
    {
      id: 'suburb',
      label: 'Address',
      minWidth: 200,
      format: (_value, row) => {
        const addr = formatAddress({
          unit: row.unit,
          streetAddress: row.streetAddress,
          suburb: row.suburb,
          state: row.state,
          postcode: row.postcode,
          country: row.country,
        });
        return addr || '-';
      },
    },
    {
      id: 'latitude',
      label: 'Map pin',
      minWidth: 140,
      format: (_value, row) => {
        const hasPin = typeof row.latitude === 'number' && typeof row.longitude === 'number';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip
              icon={<PlaceIcon />}
              label={hasPin ? 'Set' : 'Not set'}
              size="small"
              color={hasPin ? 'success' : 'default'}
              variant="outlined"
            />
            <Tooltip title="Update map pin from the address">
              <span>
                <IconButton
                  size="small"
                  aria-label={`Update map pin for ${row.name}`}
                  disabled={regeocodingId === row.id}
                  onClick={(e) => { e.stopPropagation(); void handleUpdateMapPin(row); }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      id: 'phone',
      label: 'Phone',
      minWidth: 120,
      format: (value) => (value as string) || '-',
    },
    {
      // API returns the count under the PascalCase relation `_count.Room`.
      id: '_count.Room',
      label: 'Rooms',
      minWidth: 80,
      align: 'center',
      format: (value) => (value as number) ?? 0,
    },
    {
      id: 'isActive',
      label: 'Status',
      minWidth: 100,
      align: 'right',
      format: (value) =>
        value ? (
          <Chip label="Active" size="small" color="success" />
        ) : (
          <Chip label="Inactive" size="small" color="default" />
        ),
    },
  ];

  // Handlers
  const handleAdd = () => {
    setEditingLocation(null);
    setFormData(DEFAULT_FORM_DATA);
    setModalOpen(true);
  };

  const handleEdit = (location: Location) => {
    setEditingLocation(location);
    setFormData({
      name: location.name,
      streetAddress: location.streetAddress || '',
      unit: location.unit || '',
      suburb: location.suburb || '',
      state: location.state || '',
      postcode: location.postcode || '',
      country: location.country || 'AU',
      phone: location.phone || '',
      isActive: location.isActive,
    });
    setModalOpen(true);
  };

  const handleDelete = (location: Location) => {
    setLocationToDelete(location);
    setDeleteDialogOpen(true);
  };

  const handleCountryChange = (countryCode: string) => {
    // Reset state when country changes (as states differ per country)
    setFormData({
      ...formData,
      country: countryCode,
      state: '',
    });
  };

  const handleSubmit = async () => {
    try {
      const data = {
        name: formData.name,
        streetAddress: formData.streetAddress || undefined,
        unit: formData.unit || undefined,
        suburb: formData.suburb || undefined,
        state: formData.state || undefined,
        postcode: formData.postcode || undefined,
        country: formData.country,
        phone: formData.phone || undefined,
        isActive: formData.isActive,
      };

      if (editingLocation) {
        await updateMutation.mutateAsync({
          id: editingLocation.id,
          data,
        });
      } else {
        await createMutation.mutateAsync(data);
      }
      setModalOpen(false);
    } catch {
      // Error is handled by mutation
    }
  };

  // Check if location can be deleted (must be inactive)
  const canDeleteLocation = (location: Location) => !location.isActive;

  const handleConfirmDelete = async () => {
    if (locationToDelete) {
      try {
        await deleteMutation.mutateAsync(locationToDelete.id);
        setDeleteDialogOpen(false);
        setLocationToDelete(null);
      } catch (err: unknown) {
        const axiosErr = err as { response?: { data?: { message?: string; details?: { blockingClasses?: { id: string; name: string }[] } } } };
        const classes = axiosErr?.response?.data?.details?.blockingClasses;
        if (classes && classes.length > 0) {
          setDeleteDialogOpen(false);
          setBlockingClasses(classes);
        }
        // Non-blocking-classes errors fall through — no snackbar needed here,
        // the generic "inactive first" case is handled by canDelete guard in the UI
      }
    }
  };

  const handleCopyClasses = () => {
    if (!blockingClasses) return;
    navigator.clipboard.writeText(blockingClasses.map((c) => c.name).join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isFormLoading = createMutation.isPending || updateMutation.isPending;
  const isDeleteLoading = deleteMutation.isPending;

  return (
    <Box>
      <PageHeader
        title="Locations"
        subtitle="Manage school locations and addresses"
        breadcrumbs={[
          { label: 'Admin', path: '/admin' },
          { label: 'Locations' },
        ]}
        actionLabel="Add Location"
        onAction={handleAdd}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load locations. Please try again.
        </Alert>
      )}

      {/* Filters */}
      <Stack
        direction="row"
        spacing={2}
        sx={{ mb: 2 }}
        alignItems="center"
      >
        <FormControlLabel
          control={
            <Switch
              checked={hideInactive}
              onChange={(e) => setHideInactive(e.target.checked)}
              size="small"
            />
          }
          label="Hide inactive locations"
        />

        {filteredLocations.length !== (locations?.length ?? 0) && (
          <Chip
            label={`Showing ${filteredLocations.length} of ${locations?.length ?? 0} locations`}
            size="small"
            variant="outlined"
          />
        )}
      </Stack>

      <DataTable
        columns={columns}
        data={filteredLocations}
        loading={isLoading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        canDelete={canDeleteLocation}
        deleteDisabledTooltip="Set status to Inactive to delete this location"
        emptyMessage={
          locations && locations.length > 0
            ? 'No locations match the current filter.'
            : "No locations configured. Click 'Add Location' to create one."
        }
        searchPlaceholder="Search locations..."
      />

      {/* Add/Edit Modal */}
      <FormModal
        open={modalOpen}
        title={editingLocation ? 'Edit Location' : 'Add Location'}
        submitLabel={editingLocation ? 'Update' : 'Create'}
        loading={isFormLoading}
        onSubmit={handleSubmit}
        onClose={() => setModalOpen(false)}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Location Name"
            placeholder="e.g., Main Studio"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            fullWidth
          />

          {/* Country Selector */}
          <Autocomplete
            value={countries.find((c) => c.code === formData.country) ?? countries[0]}
            onChange={(_, newValue) => handleCountryChange(newValue?.code || 'AU')}
            options={countries}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.code === value.code}
            renderInput={(params) => (
              <TextField {...params} label="Country" required />
            )}
            disableClearable
          />

          {/* Street Address */}
          <TextField
            label="Street Address"
            placeholder="e.g., 623/625 Military Rd"
            value={formData.streetAddress}
            onChange={(e) => setFormData({ ...formData, streetAddress: e.target.value })}
            fullWidth
          />

          {/* Unit/Suite */}
          <TextField
            label="Unit / Suite / Level"
            placeholder="e.g., Level 1, Suite 4"
            value={formData.unit}
            onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
            fullWidth
          />

          {/* Suburb/City */}
          <TextField
            label="Suburb / City"
            placeholder="e.g., Mosman"
            value={formData.suburb}
            onChange={(e) => setFormData({ ...formData, suburb: e.target.value })}
            fullWidth
          />

          {/* State and Postcode Row */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              {stateOptions.length > 0 ? (
                <Autocomplete
                  value={stateOptions.find((s) => s.code === formData.state) || null}
                  onChange={(_, newValue) =>
                    setFormData({ ...formData, state: newValue?.code || '' })
                  }
                  options={stateOptions}
                  getOptionLabel={(option) => `${option.name} (${option.code})`}
                  isOptionEqualToValue={(option, value) => option.code === value.code}
                  renderInput={(params) => (
                    <TextField {...params} label={stateLabel} />
                  )}
                />
              ) : (
                <TextField
                  label={stateLabel}
                  placeholder={`Enter ${stateLabel.toLowerCase()}`}
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  fullWidth
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={postcodeLabel}
                placeholder={formData.country === 'AU' ? 'e.g., 2088' : ''}
                value={formData.postcode}
                onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                fullWidth
              />
            </Grid>
          </Grid>

          {/* Phone */}
          <TextField
            label="Phone"
            placeholder="(02) 1234 5678"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            fullWidth
          />

          {/* Active Status (only shown when editing) */}
          {editingLocation && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label={formData.isActive ? 'Active' : 'Inactive'}
            />
          )}
        </Box>
      </FormModal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Location"
        message={`Are you sure you want to delete "${locationToDelete?.name}"? This will also delete all rooms at this location.`}
        confirmLabel="Delete"
        confirmColor="error"
        loading={isDeleteLoading}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />

      {/* Blocking classes error dialog */}
      <Dialog
        open={!!blockingClasses}
        onClose={() => setBlockingClasses(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cannot Delete Location</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            The following {blockingClasses?.length} class{blockingClasses?.length === 1 ? '' : 'es'} are
            assigned to rooms at this location. Reassign or remove them before deleting.
          </Alert>
          <List dense disablePadding>
            {blockingClasses?.map((cls) => (
              <ListItem key={cls.id} sx={{ py: 0.25 }}>
                <ListItemText primary={cls.name} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Tooltip title={copied ? 'Copied!' : 'Copy class list'}>
            <Button
              startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
              onClick={handleCopyClasses}
              color={copied ? 'success' : 'inherit'}
            >
              {copied ? 'Copied' : 'Copy List'}
            </Button>
          </Tooltip>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setBlockingClasses(null)} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
